// src/pages/FolhaVisita.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, PlusCircle, Eye, Trash2, Home, Check, X, FileText, ClipboardList, Plus, Calendar, GripVertical } from 'lucide-react';
import { generateVisitPDF } from '../utils/pdfGenerator';
import PDFPreviewModal from '../components/PDFPreviewModal';
import { apiFetch } from '../utils/apiFetch';
import '../Acompanhamento.css';
import './FolhaVisita.css';

const calculateAge = (dtNascimento) => {
    if (!dtNascimento) return '';
    const birthDate = new Date(dtNascimento); const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
};

const getAddressKey = (p) => {
    const rua = (p.endereco || 'ZZ_SEM_ENDERECO').trim().toLowerCase();
    const num = (p.numero || 'S/N').trim().toLowerCase();
    const comp = (p.complemento || '').trim().toLowerCase();
    return `${rua}|${num}|${comp}`;
};

const formatAddressDisplay = (p) => {
    const rua = (p.endereco || 'Endereço n/d').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    const num = p.numero ? `, ${p.numero}` : '';
    const comp = p.complemento ? ` (${p.complemento})` : '';
    return `${rua}${num}${comp}`;
};

const getConditionsString = (p) => {
    const conds = [];
    if (p.is_hipertenso) conds.push('HA');
    if (p.is_diabetico) conds.push('DM');
    if (p.is_gestante) conds.push('GE');
    if ((p.idade !== undefined ? p.idade : calculateAge(p.dt_nascimento)) >= 60) conds.push('ID');
    if (p.is_acamado) conds.push('AC');
    return conds.join(', ');
};

const buildSlimFolhaMember = (p) => ({
    cns: p.cns,
    cpf: p.cpf, 
    dt_nascimento: p.dt_nascimento, 
    nome: p.nome,
    reason: p.reason || getConditionsString(p),
    isSelected: p.isSelected !== undefined ? p.isSelected : true,
    idade: p.idade !== undefined ? p.idade : calculateAge(p.dt_nascimento),
    is_hipertenso: p.is_hipertenso || false,
    is_diabetico: p.is_diabetico || false,
    is_gestante: p.is_gestante || false,
});

const ConditionBadge = ({ type, active }) => {
    if (!active) return null;
    const colors = { DM: { bg: '#fee2e2', text: '#991b1b' }, HAS: { bg: '#e0f2fe', text: '#075985' }, Gestante: { bg: '#fce7f3', text: '#9d174d' }, Idoso: { bg: '#f1f5f9', text: '#475569' }};
    return <span style={{ background: colors[type]?.bg || '#eee', color: colors[type]?.text || '#333', fontSize: '0.6rem', padding: '1px 5px', borderRadius: '4px', fontWeight: '700', letterSpacing: '0.03em', marginRight: '4px' }}>{type}</span>;
};

let cachedTemplate = null;
const loadTemplate = async () => {
    if (cachedTemplate) return cachedTemplate;
    try {
        const response = await fetch('/templates/template-folha-visita.pdf');
        if (!response.ok) throw new Error('Template não encontrado');
        cachedTemplate = await response.arrayBuffer();
        return cachedTemplate;
    } catch (error) { console.error('Erro ao carregar template:', error); return null; }
};

export default function FolhaVisita() {
    const navigate = useNavigate();

    const gerarTituloData = () => {
        const d = new Date();
        return `Roteiro ${d.getDate()}/${d.getMonth() + 1}`;
    };

    const roteiroPadrao = { id: Date.now().toString(), titulo: gerarTituloData(), domicilios: [] };
    
    const [roteiros, setRoteiros] = useState(() => {
        try {
            const local = JSON.parse(localStorage.getItem('roteiros_salvos'));
            return (local && local.length > 0) ? local : [roteiroPadrao];
        } catch { return [roteiroPadrao]; }
    });
    
    const [activeTabId, setActiveTabId] = useState(roteiros[0]?.id || roteiroPadrao.id);
    const activeRoteiro = roteiros.find(r => r.id === activeTabId) || roteiros[0];

    const [isCloudSyncing, setIsCloudSyncing] = useState(true);
    const [allPatients, setAllPatients] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pendingQueue, setPendingQueue] = useState([]);
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pdfData, setPdfData] = useState(null);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [currentUser, setCurrentUser] = useState({});
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [syncStatus, setSyncStatus] = useState('⏳ Inicializando...');

    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dropIndex, setDropIndex] = useState(null); 
    const [isLayoutCollapsed, setIsLayoutCollapsed] = useState(false);
    const [setDraggableKey] = useState(null);
    
    const [includeBlankPage, setIncludeBlankPage] = useState(false);

    useEffect(() => {
        const fetchCloudOnLoad = async () => {
            setIsCloudSyncing(true);
            setSyncStatus('⏳ Baixando da nuvem...');
            try {
                const response = await apiFetch('/api/usuarios/rascunho');
                const data = await response.json();
                const rascunho = data.folha || [];

                if (rascunho.length > 0) {
                    setRoteiros(rascunho);
                    setActiveTabId(rascunho[0].id);
                    localStorage.setItem('roteiros_salvos', JSON.stringify(rascunho));
                    setSyncStatus('☁️ Sincronizado');
                } else {
                    setSyncStatus('☁️ Nuvem vazia');
                }
            } catch (err) {
                setSyncStatus('⚠️ Trabalho Off-line');
            } finally {
                setIsCloudSyncing(false);
            }
        };
        fetchCloudOnLoad();
    }, []);

    useEffect(() => {
        loadTemplate().catch(err => console.warn('Template não disponível:', err));
        const userStr = localStorage.getItem('user');
        if (userStr) { try { setCurrentUser(JSON.parse(userStr)); } catch (e) {} }

        const queueData = localStorage.getItem('visitQueueData');
        if (queueData) { try { setPendingQueue(JSON.parse(queueData)); } catch (e) {} }

        const fetchAllPatients = async () => {
            setIsLoadingData(true);
            try {
                const response = await apiFetch('/api/v2/pacientes');
                const result = await response.json();
                const data = (result.data || []).map(p => ({ 
                    ...p, 
                    idade: (p.idade !== undefined && p.idade !== -1) ? p.idade : calculateAge(p.dt_nascimento) 
                }));
                setAllPatients(data);
            } catch (error) { console.error('Erro ao buscar pacientes:', error); }
            finally { setIsLoadingData(false); }
        };
        fetchAllPatients();
    }, []);

    const searchResults = useMemo(() => {
        if (searchTerm.trim().length < 3) return [];
        const cleanTerm = searchTerm.toLowerCase().replace(/,/g, ' ');

        const households = {};
        allPatients.forEach(p => {
            const key = getAddressKey(p);
            if (!households[key]) households[key] = [];
            households[key].push(p);
        });

        const results = [];
        Object.entries(households).forEach(([key, members]) => {
            if (activeRoteiro.domicilios.some(v => v.key === key)) return;
            const rep = members[0];
            const addressStr = `${rep.endereco || ''} ${rep.numero || ''} ${rep.complemento || ''}`.toLowerCase().replace(/,/g, ' ');
            const addressMatch = addressStr.includes(cleanTerm);
            const memberMatch = members.some(m => m.nome.toLowerCase().includes(cleanTerm));

            if (addressMatch || memberMatch) {
                const sortedMembers = members.sort((a, b) => b.idade - a.idade);
                results.push({ key, addressDisplay: formatAddressDisplay(rep), members: sortedMembers, matchType: memberMatch ? 'Nome' : 'Endereço' });
            }
        });

        return results.slice(0, 8);
    }, [searchTerm, allPatients, activeRoteiro.domicilios]);

    useEffect(() => {
        if (isCloudSyncing) return;
        localStorage.setItem('roteiros_salvos', JSON.stringify(roteiros));
        setSyncStatus('⏳ Pendente...');
        const timer = setTimeout(async () => {
            setSyncStatus('⏳ Salvando...');
            try {
                await apiFetch('/api/usuarios/rascunho', { method: 'PUT', body: JSON.stringify({ tipo: 'folha', dados: roteiros }) });
                setSyncStatus('☁️ Salvo na nuvem');
            } catch { setSyncStatus('⚠️ Erro de Sincronização'); }
        }, 3000);
        return () => clearTimeout(timer);
    }, [roteiros, isCloudSyncing]);

    const puxarDaNuvem = async () => {
        setIsCloudSyncing(true);
        setSyncStatus('⏳ Baixando...');
        try {
            const response = await apiFetch('/api/usuarios/rascunho');
            const data = await response.json();
            const rascunho = data.folha || [];
            if (rascunho.length > 0) {
                setRoteiros(rascunho);
                setActiveTabId(rascunho[0].id);
                localStorage.setItem('roteiros_salvos', JSON.stringify(rascunho));
                setSyncStatus('☁️ Sincronizado!');
            } else {
                alert('Nenhum roteiro salvo na nuvem para o seu usuário.');
                setSyncStatus('☁️ Nuvem vazia');
            }
        } catch (err) { setSyncStatus('⚠️ Erro ao baixar'); }
        finally { setIsCloudSyncing(false); }
    };

    const adicionarNovoRoteiro = () => {
        if (roteiros.length >= 5) { alert('Você pode criar no máximo 5 roteiros simultâneos.'); return; }
        const novo = { id: Date.now().toString(), titulo: `Roteiro ${roteiros.length + 1}`, domicilios: [] };
        setRoteiros([...roteiros, novo]);
        setActiveTabId(novo.id);
    };

    const removerRoteiroAtual = (id) => {
        if (window.confirm('Tem certeza que deseja apagar esta aba inteira?')) {
            const novos = roteiros.filter(r => r.id !== id);
            if (novos.length === 0) {
                const padrao = { id: Date.now().toString(), titulo: gerarTituloData(), domicilios: [] };
                setRoteiros([padrao]);
                setActiveTabId(padrao.id);
            } else {
                setRoteiros(novos);
                setActiveTabId(novos[0].id);
            }
        }
    };

    const updateActiveRoteiro = (updatedDomicilios) => {
        setRoteiros(prev => prev.map(r => r.id === activeTabId ? { ...r, domicilios: updatedDomicilios } : r));
    };

    const handleAddHouse = (house) => {
        const newItem = { key: house.key, addressDisplay: house.addressDisplay, members: house.members.map(buildSlimFolhaMember) };
        updateActiveRoteiro([...activeRoteiro.domicilios, newItem]);
        setSearchTerm('');
    };

    const handleRemoveHouse = (key) => {
        updateActiveRoteiro(activeRoteiro.domicilios.filter(h => h.key !== key));
    };

    const handleMemberChange = (houseKey, cns, field, value) => {
        const novos = activeRoteiro.domicilios.map(house => {
            if (house.key !== houseKey) return house;
            return { ...house, members: house.members.map(m => m.cns === cns ? { ...m, [field]: value } : m) };
        });
        updateActiveRoteiro(novos);
    };

    const mudarTituloRoteiro = (e) => {
        setRoteiros(prev => prev.map(r => r.id === activeTabId ? { ...r, titulo: e.target.value } : r));
    };

    const clearQueue = () => {
        setPendingQueue([]);
        localStorage.removeItem('visitQueueData');
    };

    // 🔥 AUTO-HEALING APLICADO NO BOTÃO DE LANÇAR
    const enviarParaLancamento = () => {
        if (activeRoteiro.domicilios.length === 0) { alert('Adicione domicílios neste roteiro antes de lançar.'); return; }
        
        const rotaParaLancar = activeRoteiro.domicilios.map(house => ({
            ...house,
            members: house.members.filter(m => m.isSelected).map(m => {
                // Preenche dados perdidos vasculhando o banco local
                const fullPatientData = allPatients.find(p => p.cns === m.cns || p.nome === m.nome);
                return {
                    ...m,
                    cpf: m.cpf || (fullPatientData ? fullPatientData.cpf : ''),
                    dt_nascimento: m.dt_nascimento || (fullPatientData ? fullPatientData.dt_nascimento : '')
                };
            })
        })).filter(house => house.members.length > 0);

        if(rotaParaLancar.length === 0){ alert('Selecione ao menos um paciente!'); return;}

        localStorage.setItem('currentVisitList', JSON.stringify(rotaParaLancar));
        navigate('/lancamento', { state: { importFromFolha: true } });
    };

    // 🔥 AUTO-HEALING APLICADO NO GERADOR DE PDF
    const handlePreview = async (withBlank = includeBlankPage) => {
        if (activeRoteiro.domicilios.length === 0) { alert('Aba vazia!'); return; }
        setIsGeneratingPDF(true);
        try {
            const acsInfo = { nome: currentUser.nome || 'ACS', equipe: '06', microarea: currentUser.microarea || '01' };
            const templateBytes = await loadTemplate();
            if (!templateBytes) { alert('Erro: Template PDF não encontrado.'); return; }
            
            // "Cura" a lista em memória antes de entregar para o gerador de PDF
            const domiciliosEnriquecidos = activeRoteiro.domicilios.map(house => ({
                ...house,
                members: house.members.map(m => {
                    const fullPatientData = allPatients.find(p => p.cns === m.cns || p.nome === m.nome);
                    return {
                        ...m,
                        cpf: m.cpf || (fullPatientData ? fullPatientData.cpf : ''),
                        dt_nascimento: m.dt_nascimento || (fullPatientData ? fullPatientData.dt_nascimento : '')
                    };
                })
            }));

            const blob = await generateVisitPDF(domiciliosEnriquecidos, acsInfo, 'blob', templateBytes, withBlank);
            const url = URL.createObjectURL(blob);
            setPdfBlob(blob); setPdfData(url); setIsModalOpen(true);
        } catch (error) { 
            console.error(error); alert('Erro ao gerar PDF: ' + error.message); 
        } finally { setIsGeneratingPDF(false); }
    };

    const handleToggleBlankPage = (newVal) => {
        setIncludeBlankPage(newVal);
        handlePreview(newVal); 
    };

    const executeDrop = () => {
        if (draggedIndex === null || dropIndex === null) return;
        
        let finalIndex = dropIndex;
        if (draggedIndex < dropIndex) {
            finalIndex--;
        }

        if (finalIndex !== draggedIndex) {
            const novaLista = [...activeRoteiro.domicilios];
            const draggedItem = novaLista[draggedIndex];
            novaLista.splice(draggedIndex, 1);
            novaLista.splice(finalIndex, 0, draggedItem);
            updateActiveRoteiro(novaLista);
        }

        setDraggedIndex(null);
        setDropIndex(null);
        setIsLayoutCollapsed(false);
        setDraggableKey(null);
    };

    const handleDragOverScroll = (e) => {
        const container = document.querySelector('.folha-visita-content'); 
        if (!container) return;
        const threshold = 80; 
        const speed = 15; 
        const rect = container.getBoundingClientRect();
        
        if (e.clientY - rect.top < threshold) container.scrollTop -= speed;
        else if (rect.bottom - e.clientY < threshold) container.scrollTop += speed;
    };

    const totalMembros = activeRoteiro.domicilios.reduce((acc, h) => acc + h.members.filter(m => m.isSelected).length, 0);

    return (
        <div className="folha-visita-page">
            <header className="header">
                <div>
                    <h1 className="page-title">
                        <div style={{ width: '32px', height: '32px', background: 'rgba(255,255,255,0.2)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FileText size={18} color="white" /></div>
                        Roteiro de Visita
                    </h1>
                    <p className="url-path">Pesquise pacientes ou endereços. Crie abas para organizar sua semana.</p>
                </div>
                <div className="header-actions">
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: syncStatus.includes('Erro') ? 'rgba(239,68,68,0.2)' : 'rgba(0,0,0,0.18)', color: syncStatus.includes('Erro') ? '#fca5a5' : 'rgba(255,255,255,0.9)', padding: '4px 10px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
                        {syncStatus}
                    </div>
                    <button onClick={puxarDaNuvem} disabled={isCloudSyncing} style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', padding: '8px 14px', borderRadius: '8px', fontWeight: '700', cursor: isCloudSyncing ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', opacity: isCloudSyncing ? 0.6 : 1 }}>☁️ Forçar Sincronização</button>
                    <button onClick={enviarParaLancamento} style={{ background: activeRoteiro.domicilios.length > 0 ? 'linear-gradient(135deg, #047857, #059669)' : '#e2e8f0', color: activeRoteiro.domicilios.length > 0 ? 'white' : '#94a3b8', border: 'none', padding: '8px 16px', borderRadius: '8px', fontWeight: '700', cursor: activeRoteiro.domicilios.length > 0 ? 'pointer' : 'not-allowed', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', boxShadow: activeRoteiro.domicilios.length > 0 ? '0 2px 8px rgba(5,150,105,0.3)' : 'none', transition: 'all 0.15s' }}>
                        <ClipboardList size={15} /> Lançar Roteiro Atual
                        {totalMembros > 0 && <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '10px', padding: '1px 6px', fontSize: '0.68rem', fontWeight: '800' }}>{totalMembros}</span>}
                    </button>
                    <button className="preview-btn" onClick={() => handlePreview(includeBlankPage)} disabled={activeRoteiro.domicilios.length === 0 || isGeneratingPDF}>
                        <Eye size={15} /> {isGeneratingPDF ? 'Gerando...' : 'Gerar PDF'}
                    </button>
                </div>
            </header>

            <div style={{ display: 'flex', alignItems: 'center', padding: '0 20px', borderBottom: '1px solid #e2e8f0', background: 'white', gap: '8px', overflowX: 'auto' }}>
                {roteiros.map(r => (
                    <div key={r.id} onClick={() => setActiveTabId(r.id)} style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: activeTabId === r.id ? '3px solid #059669' : '3px solid transparent', color: activeTabId === r.id ? '#059669' : '#64748b', fontWeight: activeTabId === r.id ? '700' : '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
                        <Calendar size={16} />
                        {activeTabId === r.id ? (
                            <input type="text" id={`titulo_aba_${r.id}`} name={`titulo_aba_${r.id}`} value={r.titulo} onChange={mudarTituloRoteiro} style={{ border: 'none', background: 'transparent', outline: 'none', color: 'inherit', fontWeight: 'inherit', width: '90px' }} />
                        ) : ( r.titulo )}
                        {roteiros.length > 1 && activeTabId === r.id && (
                            <button onClick={(e) => { e.stopPropagation(); removerRoteiroAtual(r.id); }} style={{ background: '#fee2e2', border: 'none', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginLeft: '4px', padding: 0 }} title="Apagar Roteiro"><X size={12} color="#dc2626" /></button>
                        )}
                    </div>
                ))}
                {roteiros.length < 5 && (
                    <button onClick={adicionarNovoRoteiro} style={{ background: 'transparent', border: '1px dashed #cbd5e1', padding: '6px 12px', borderRadius: '6px', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', marginLeft: '8px' }}><Plus size={14} /> Novo Roteiro</button>
                )}
            </div>

            <div className="folha-visita-content" style={{ marginTop: '0' }}
                 onDragOver={(e) => {
                     e.preventDefault();
                     handleDragOverScroll(e);
                     if (e.target === e.currentTarget && draggedIndex !== null) {
                         setDropIndex(activeRoteiro.domicilios.length);
                     }
                 }}
                 onDrop={(e) => {
                     e.preventDefault();
                     executeDrop();
                 }}
            >
                <div className="visit-controls">
                    <div className="search-bar-wrapper">
                        <Search className="search-icon-input" size={20} />
                        <input type="text" id="busca_pacientes_folha" name="busca_pacientes_folha" autoComplete="off" placeholder={isLoadingData ? "Carregando pacientes..." : "Busque pacientes ou endereços (ex: Rua 1, 100)..."} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} disabled={isLoadingData} className="main-search-input" />
                        {searchResults.length > 0 && (
                            <div className="dropdown-results">
                                {searchResults.map(res => (
                                    <div key={res.key} className="dropdown-item" onClick={() => handleAddHouse(res)}>
                                        <div className="dropdown-icon"><Home size={15} color="#059669" /></div>
                                        <div className="dropdown-info"><strong>{res.addressDisplay}</strong><span>{res.members.length} moradores (Busca por {res.matchType})</span></div>
                                        <PlusCircle size={17} className="add-icon" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {pendingQueue.length > 0 && (
                    <div className="pending-queue-box">
                        <div className="pending-queue-header">
                            <h3 className="pending-queue-title"><Check size={18} /> Importar da Fila ({pendingQueue.length})</h3>
                            <div className="pending-queue-actions"><button className="queue-btn-clear" onClick={clearQueue}>Limpar Fila</button></div>
                        </div>
                        <p style={{fontSize: '0.8rem', color: '#64748b', marginBottom: '10px'}}>Pesquise as casas acima manualmente para adicioná-las aos seus roteiros.</p>
                    </div>
                )}

                <div className="visit-list-container">
                    {activeRoteiro.domicilios.length === 0 ? (
                        <div className="empty-sheet">
                            <div style={{ width: '56px', height: '56px', background: '#f0f9f4', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><Home size={24} color="#a7f3d0" /></div>
                            <p>Aba "{activeRoteiro.titulo}" está vazia.</p>
                            <p style={{ fontSize: '0.82rem', marginTop: '6px', color: '#b0bec5' }}>Busque por endereço ou paciente na barra acima.</p>
                        </div>
                    ) : (
                        activeRoteiro.domicilios.map((house, index) => {
                            const isBeingDragged = draggedIndex === index;
                            
                            const isDropTargetBefore = dropIndex === index && draggedIndex !== index && draggedIndex !== index - 1;
                            const isLastCard = index === activeRoteiro.domicilios.length - 1;
                            const isDropTargetAfter = isLastCard && dropIndex === activeRoteiro.domicilios.length && draggedIndex !== index;

                            let shadowStyle = '0 2px 6px rgba(0,0,0,0.05)'; 
                            if (isDropTargetBefore) shadowStyle = '0 -5px 0 0 #16a34a, 0 2px 6px rgba(0,0,0,0.05)';
                            else if (isDropTargetAfter) shadowStyle = '0 5px 0 0 #16a34a, 0 2px 6px rgba(0,0,0,0.05)';

                            return (
                                <div 
                                    key={house.key} 
                                    className="house-card-edit"
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation(); 
                                        handleDragOverScroll(e);

                                        if (draggedIndex === null) return;

                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const midY = rect.top + rect.height / 2;
                                        
                                        let newDropIndex = index;
                                        if (e.clientY > midY) {
                                            newDropIndex = index + 1;
                                        }

                                        if (dropIndex !== newDropIndex) {
                                            setDropIndex(newDropIndex);
                                        }
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        executeDrop();
                                    }}
                                    style={{
                                        opacity: isBeingDragged ? 0.3 : 1,
                                        transform: isBeingDragged ? 'scale(0.98)' : 'none',
                                        boxShadow: shadowStyle,
                                        transition: 'all 0.15s ease',
                                        zIndex: isBeingDragged ? 10 : 1
                                    }}
                                >
                                    <div 
                                        className="house-header" 
                                        draggable={true} 
                                        onDragStart={(e) => {
                                            setDraggedIndex(index);
                                            setDropIndex(index);
                                            setDraggableKey(house.key);
                                            if(e.dataTransfer) {
                                                e.dataTransfer.effectAllowed = 'move';
                                                e.dataTransfer.setData('text/plain', index); 
                                            }
                                            setTimeout(() => setIsLayoutCollapsed(true), 10);
                                        }}
                                        onDragEnd={() => { 
                                            setDraggedIndex(null); 
                                            setDropIndex(null);
                                            setIsLayoutCollapsed(false);
                                            setDraggableKey(null);
                                        }}
                                        style={{ cursor: 'grab' }} 
                                        title="Puxe por aqui para reordenar a visita"
                                    >
                                        <div className="house-title">
                                            <GripVertical size={16} color="#94a3b8" style={{ marginRight: '6px' }} />
                                            <div className="house-title-icon"><Home size={15} color="#059669" /></div>
                                            <span>{house.addressDisplay}</span>
                                            <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#94a3b8' }}>({house.members.length} moradores)</span>
                                        </div>
                                        <button className="remove-btn" onClick={() => handleRemoveHouse(house.key)}><Trash2 size={15} /></button>
                                    </div>

                                    {!isLayoutCollapsed && (
                                        <div className="house-members-table">
                                            {house.members.map(member => (
                                                <div key={member.cns} className={`member-row ${!member.isSelected ? 'disabled' : ''}`}>
                                                    <div className="col-check">
                                                        <input type="checkbox" id={`check_${member.cns}`} name={`check_${member.cns}`} checked={member.isSelected} onChange={(e) => handleMemberChange(house.key, member.cns, 'isSelected', e.target.checked)} />
                                                    </div>
                                                    <div className="col-info">
                                                        <div className="member-name">{member.nome}</div>
                                                        <div className="member-badges">
                                                            <ConditionBadge type="HAS" active={member.is_hipertenso} />
                                                            <ConditionBadge type="DM" active={member.is_diabetico} />
                                                            <ConditionBadge type="Gestante" active={member.is_gestante} />
                                                            <ConditionBadge type="Idoso" active={member.idade >= 60} />
                                                        </div>
                                                    </div>
                                                    <div className="col-input" style={{ flex: 2 }}>
                                                        <input type="text" id={`reason_${member.cns}`} name={`reason_${member.cns}`} value={member.reason} disabled={!member.isSelected} placeholder="Ex: Aferir PA, Entregar receita..." onChange={(e) => handleMemberChange(house.key, member.cns, 'reason', e.target.value)} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            <PDFPreviewModal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); if (pdfData) URL.revokeObjectURL(pdfData); }}
                pdfData={pdfData}
                pdfBlob={pdfBlob}
                includeBlankPage={includeBlankPage}
                onToggleBlankPage={handleToggleBlankPage}
            />
        </div>
    );
}